import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

function StatusBadge({ status }) {
  return (
    <Badge
      variant={status === "active" ? "default" : "secondary"}
      className="rounded-sm text-[10px] uppercase tracking-wider"
    >
      {status}
    </Badge>
  );
}

export default function Registry() {
  const { t } = useTranslation();
  const [modules, setModules] = useState([]);
  const [caps, setCaps] = useState([]);
  const [provs, setProvs] = useState([]);

  useEffect(() => {
    api.get("/registry/modules").then((r) => setModules(r.data)).catch(() => {});
    api.get("/registry/capabilities").then((r) => setCaps(r.data)).catch(() => {});
    api.get("/registry/providers").then((r) => setProvs(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <PageHeader title={t("registry.title")} subtitle={t("registry.subtitle")} />
      <Tabs defaultValue="modules">
        <TabsList className="rounded-sm">
          <TabsTrigger value="modules" className="rounded-sm" data-testid="tab-modules">{t("registry.modules")}</TabsTrigger>
          <TabsTrigger value="capabilities" className="rounded-sm" data-testid="tab-capabilities">{t("registry.capabilities")}</TabsTrigger>
          <TabsTrigger value="providers" className="rounded-sm" data-testid="tab-providers">{t("registry.providers")}</TabsTrigger>
        </TabsList>

        <TabsContent value="modules">
          <div className="bg-card border border-border rounded-sm mt-4 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>{t("common.name")}</TableHead>
                  <TableHead>{t("dashboard.milestone")}</TableHead>
                  <TableHead>{t("common.status")}</TableHead>
                  <TableHead>{t("registry.dependsOn")}</TableHead>
                  <TableHead>{t("registry.events")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {modules.map((m) => (
                  <TableRow key={m.id} data-testid={`registry-module-${m.id}`}>
                    <TableCell className="font-mono text-xs">{m.id}</TableCell>
                    <TableCell className="font-medium">{m.name}</TableCell>
                    <TableCell className="font-mono text-xs">{m.milestone}</TableCell>
                    <TableCell><StatusBadge status={m.status} /></TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{m.depends_on?.join(", ") || "—"}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{m.events_emitted?.join(", ") || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="capabilities">
          <div className="bg-card border border-border rounded-sm mt-4 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>{t("registry.category")}</TableHead>
                  <TableHead>{t("registry.version")}</TableHead>
                  <TableHead>{t("common.status")}</TableHead>
                  <TableHead>{t("registry.costHint")}</TableHead>
                  <TableHead>Provider</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {caps.map((c) => (
                  <TableRow key={c.id} data-testid={`registry-capability-${c.id}`}>
                    <TableCell className="font-mono text-xs">{c.id}</TableCell>
                    <TableCell><Badge variant="outline" className="rounded-sm">{c.category}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{c.version}</TableCell>
                    <TableCell><StatusBadge status={c.status} /></TableCell>
                    <TableCell className="font-mono text-xs">{c.cost_hint}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{c.provider_ref || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="providers">
          <div className="bg-card border border-border rounded-sm mt-4 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>{t("registry.kind")}</TableHead>
                  <TableHead>{t("common.name")}</TableHead>
                  <TableHead>{t("registry.version")}</TableHead>
                  <TableHead>{t("registry.lifecycle")}</TableHead>
                  <TableHead>{t("registry.compatibility")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {provs.map((p) => (
                  <TableRow key={p.id} data-testid={`registry-provider-${p.id}`}>
                    <TableCell className="font-mono text-xs">{p.id}</TableCell>
                    <TableCell><Badge variant="outline" className="rounded-sm">{p.kind}</Badge></TableCell>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell className="font-mono text-xs">{p.version}</TableCell>
                    <TableCell><StatusBadge status={p.lifecycle} /></TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{p.compatibility?.join(", ") || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
